import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { FundStatus } from '@/models/common/FundStatus';
import useFundContext from '@/contexts/Fund/useFundContext';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { SupportRequestExportDetailsDtoIn } from '@/models/in/SupportRequestExportDetailsDtoIn';
import { DocumentWithDetailsDtoIn } from '@/models/in/DocumentWithDetailsDtoIn';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import AllowedFor from '@/models/common/AllowedFor';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { getAggregatedStatus, getStatusGroup } from '@/helpers/requestStatus';
import { getDetailStatusDisplayId } from '@/helpers/requestDetailStatus';
import { formatISODate, formatDateLocale } from '@/helpers/dateHelper';
import { getSupplierDisplayDetails } from '@/helpers/supplierDisplayHelper';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import { getAuth } from '@/api/ApiCalls';
import DataTable from '@/components/ui/DataTable/DataTable';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import useTableDensity from '@/hooks/useTableDensity';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';
import FiltersArea from './FiltersArea';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import CircleTextSkeleton from '@/components/ui/Skeleton/CircleTextSkeleton';
import Button from '@/components/ui/Button';
import NewSupportRequest from './NewSupportRequest';
import DraftsButton from './Drafts/DraftsButton';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import SupportRequestCard from './SupportRequestCard';
import { FabActionCard } from '@/components/ui/Fab/Fab';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import useBoardTabs from '@/components/ui/BoardTabs/useBoardTabs';
import CancelSupportRequestDialog, { hasCancellableMonth } from './CancelSupportRequestDialog/CancelSupportRequestDialog';
import ReduceSupportRequestAmountDialog, { hasReducibleMonth } from './ReduceSupportRequestAmountDialog/ReduceSupportRequestAmountDialog';
import MoveToNextMonthDialog, { canMoveToNextMonth } from './MoveToNextMonthDialog/MoveToNextMonthDialog';
import SignSupportRequestDialog from './SignSupportRequestDialog/SignSupportRequestDialog';
import EditSupportRequestDialog from './EditSupportRequestDialog/EditSupportRequestDialog';
import SupportRequestRowActions from './SupportRequestRowActions';
import deleteIcon from '@/assets/icons/delete.svg';
import calendarIcon from '@/assets/icons/calendar.svg';
import editIcon from '@/assets/icons/edit.svg';
import signatureIcon from '@/assets/icons/check.svg';
import infoIcon from '@/assets/icons/info2.svg';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import styles from './SupportRequestsPage.module.scss';
import tableStyles from '@/components/ui/DataTable/DataTable.module.scss';
import SupportRequestType from '@/models/common/SupportRequestType';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import CalendarModeProvider from '@/contexts/CalendarMode/CalendarModeProvider';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import useFundAccess from '@/hooks/useFundAccess';
import { LockIcon } from '@/pages/FundSettings/people/peopleIcons';
import useReturnedPayments, { returnedPaymentsUrl } from './ReturnedPaymentSection/useReturnedPayments';
import Alert from '@/components/ui/Alert';
import ReturnedPaymentsNotice from './ReturnedPaymentSection/ReturnedPaymentsNotice';
import ReturnedPaymentDialog from './ReturnedPaymentSection/ReturnedPaymentDialog';
import { ReturnedPaymentDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import { queryClient } from '@/hooks/QueryClient';
import bankIcon from '@/assets/icons/bank.svg';

const DENSITY_STORAGE_KEY = 'supportRequestsTableDensity';

/**
 * Resolves the "recipient" name to display in the support-requests list for fund-expense
 * requests (which have no BeneficiaryContact). Returns the payer name when the fund is
 * reimbursing a private payer, otherwise the supplier name (with legacy AdditionalDetails
 * fallback for requests created before the Suppliers table existed). Empty string when no
 * source is available (rare — old data only).
 */
const getFundExpenseRecipientName = (request: SupportRequestDtoIn): string => {
  const details = request.additionalDetails as FundExpenseDetails | undefined;
  if (details?.isAlreadyPaid) {
    return details.payerName ?? '';
  }
  const supplierDisplay = getSupplierDisplayDetails(request.supplier, request.additionalDetails);
  return supplierDisplay?.name ?? '';
};

const supportRequestSkeletonConfig = {
  goal: () => ({
    widths: ['102px'],
  }),
  supported: () => ({
    widths: ['88px'],
  }),
  sum: () => ({
    widths: ['73px'],
  }),
  paymentMethod: () => ({
    widths: ['60px'],
  }),
  date: () => ({
    widths: ['71px'],
  }),
  status: () => ({
    customRender: () => <CircleTextSkeleton circleSize={10} rows={[{ width: '56px', height: '10px' }]} gap={6} />,
  }),
};

/**
 * A support-request row enriched (at export time) with the additional-info data shown in the
 * blade's "status and documents" / "additional details" tabs. The enrichment happens only when
 * the user clicks export - the list endpoint stays lean for the regular page load.
 */
type SupportRequestExportRow = SupportRequestDtoIn & {
  exportDetails?: SupportRequestExportDetailsDtoIn;
};

const getRowExportDetails = (row: SupportRequestDtoIn) => (row as SupportRequestExportRow).exportDetails;

const getRowFundExpenseDetails = (row: SupportRequestDtoIn) =>
  row.goal?.allowedFor === AllowedFor.Fund ? (row.additionalDetails as FundExpenseDetails | undefined) : undefined;

/**
 * Renders one export-cell value per payment: a single scalar for one-time requests, or
 * "1. x | 2. y | ..." for monthly requests (one entry per payment, numbered by its counter).
 */
const joinPerPayment = (
  details: SupportRequestExportDetailsDtoIn | undefined,
  format: (subRequest: SupportRequestExportDetailsDtoIn['supportRequests'][number]) => string
): string => {
  const subRequests = details?.supportRequests ?? [];
  if (subRequests.length === 0) return '';
  if (subRequests.length === 1) return format(subRequests[0]);
  return subRequests.map((subRequest) => `${subRequest.multiRequestCounter}. ${format(subRequest)}`).join(' | ');
};

const getInitialDateRange = () => {
  const now = new Date();
  const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { dateFrom, dateTo };
};

/**
 * כל הבקשות, מהראשונה ועד סוף החודש הנוכחי.
 *
 * הטווח שהיה כאן בתוך שחזור הקישור העמוק (deep link) הועבר לפונקציה משלו, כי
 * הוא נחוץ עכשיו בשני מקומות: שם, וב-`?range=all` שמגיע מ"תמונת מצב".
 */
const getAllTimeDateRange = () => ({
  dateFrom: new Date(2015, 0, 1),
  dateTo: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
});

/**
 * מסננת לפי משך ההמתנה מאז ההגשה, מתוך  של "דורש טיפול".
 *
 * הכרטיס שם סופר בקשות שתקועות מעל מספר ימים, ולכן בלי התנאי הזה המסך
 * הראה גם בקשות שהוגשו אתמול - יותר בקשות מהמספר שנלחץ. הסינון חל על
 * הרשימה עצמה ולא על התצוגה בלבד, כך שגם מוני הסטטוסים סופרים את אותו דבר.
 */
const waitingLongerThan = (requests: SupportRequestDtoIn[], days: number): SupportRequestDtoIn[] => {
  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return requests.filter((request) => {
    const requestedAt = new Date(request.requestedAt).getTime();
    return !isNaN(requestedAt) && requestedAt < threshold;
  });
};

/**
 * מסננת ל"ממתינה לאישור הראשוני עצמו", מתוך `?initialOnly=` של "דורש טיפול".
 *
 * `isInInitialStatusProcess` לבדו רחב מדי - הוא true גם על בקשה שנדחתה,
 * בוטלה, או ממתינה להשלמת מסמכים (ראו ההערה על אותו שם ב-useDashboardData),
 * ואף אחת מאלה אינה "ממתינה לאישור" מבחינת שלמה. אותו תנאי כפול בדיוק כמו
 * בכרטיס - וחייב להישאר זהה לו, אחרת המספר שנלחץ שוב לא יהיה המספר שמתקבל.
 */
const isPendingInitialReview = (request: SupportRequestDtoIn): boolean => {
  if (!request.isInInitialStatusProcess) return false;
  return (request.statuses ?? []).some((status) => {
    const group = getStatusGroup(status)?.id;
    return group === 'PendingApproval' || group === 'PendingSignatures';
  });
};

const requestSearch: FilterFn<SupportRequestDtoIn> = (row, _columnId, value) => {
  if (!value) return true;

  const searchValue = String(value).toLowerCase();

  const request = row.original;

  // Search by full name (also matches first/last name separately) or request ID
  const normalizedSearchValue = searchValue.trim();
  const fullName = request.supportedContact
    ? `${(request.supportedContact.firstName ?? '').trim()} ${(request.supportedContact.lastName ?? '').trim()}`.replace(/\s+/g, ' ').toLowerCase()
    : '';
  // For fund-expense requests there's no supported contact - match against the
  // supplier/payer name we render in the "supported" column instead, so the search
  // bar finds these rows the same way it finds beneficiary rows.
  const fundExpenseName = request.goal?.allowedFor === AllowedFor.Fund ? getFundExpenseRecipientName(request).trim().toLowerCase() : '';
  return (
    fullName.includes(normalizedSearchValue) ||
    fundExpenseName.includes(normalizedSearchValue) ||
    getShortRequestId(request.correlationId).toLowerCase().includes(normalizedSearchValue)
  );
};

const SupportRequestsPage = () => {
  const { currentFund } = useFundContext();
  const { currentUser } = useAuthContext();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDeviceDetection();
  // כשהטאבים מוצגים הטאב הפעיל הוא הכותרת (ר' useBoardTabs).
  const { isVisible: hasBoardTabs } = useBoardTabs();
  const hasInquiries = useInquiriesFeature();
  /*
   * רוחב עמודת הפעולות תלוי בדגל הפניות.
   *
   * כשהדגל כבוי OpenInquiryButton מחזיר null, אבל הרוחב הקבוע של העמודה נמדד
   * בזמנו כשהאייקון היה שם - ולכן נשארו 40px ריקים (34px כפתור + 6px רווח)
   * שדחפו את שאר האייקונים מהקצה. שתי המחרוזות כתובות במלואן כי Tailwind
   * מייצר מחלקות מהמקור ולא מערכים שמחושבים בזמן ריצה.
   *
   * HAT-1253: הרוחב גדל, כי מאז שכל פעולה שומרת את המקום שלה התא מחזיק שלוש
   * משבצות של 34px ושני רווחים של 6px — 114px — ולא שני כפתורים ועוד תפריט
   * צר. עם ‎12px padding מכל צד זה 138px, ולכן 140px. העמודה הגמישה היחידה
   * בטבלה היא "מטרה" (min-w-[140px] בלי max), והיא זו שסופגת את ההפרש.
   */
  const rowActionsWidth = hasInquiries ? 'max-w-[140px] min-w-[140px] w-[140px]' : 'max-w-[100px] min-w-[100px] w-[100px]';
  const [selectedSupportedName, setSelectedSupportedName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [openNewSupportRequest, setOpenNewSupportRequest] = useState(false);

  /*
   * HAT-1301: a frozen fund refuses new requests on the server. Without this the coordinator could
   * still open the form, fill in a beneficiary, an amount and a payment method, and only be told
   * no at the end - for a reason that has nothing to do with anything they typed. The banner above
   * the board already explains the state; here the door is simply shut.
   */
  const isFundFrozen = currentFund?.status === FundStatus.Frozen;
  // A view-only coordinator sees the button locked with the reason, instead of filling the whole
  // form and failing at the end (15.10.2026). The server refuses it either way. Since the permission
  // worlds (22.10.2026) what counts is edit in supports, whatever the other worlds allow.
  const { canEdit } = useFundAccess(currentFund?.id);
  const isViewOnly = !canEdit('Supports');
  const openNewRequest = useCallback(() => {
    if (isFundFrozen || isViewOnly) return;
    setOpenNewSupportRequest(true);
  }, [isFundFrozen, isViewOnly]);
  /* The draft the coordinator chose to finish, if they came in through DraftsButton. */
  const [continuedDraft, setContinuedDraft] = useState<SupportRequestDraftDtoIn | null>(null);
  const [dateFilter, setDateFilter] = useState(getInitialDateRange);

  const apiUrl = useMemo(() => {
    if (!currentFund?.id) return '';
    const params = new URLSearchParams({
      dateFrom: formatISODate(dateFilter.dateFrom),
      dateTo: formatISODate(dateFilter.dateTo),
    });
    return `funds/${currentFund.id}/support-requests?${params.toString()}`;
  }, [currentFund?.id, dateFilter]);

  const { data: serverRequests, isLoading, refetch: refetchRequests } = useGetAuthApi<SupportRequestDtoIn[]>(apiUrl, undefined, {
    /*
     * חזרה ללשונית מושכת את הלוח מחדש: הרכז ממתין כאן להחלטת המשרד על בקשה
     * שהגיש, והיא משתנה אצל מישהו אחר בלי שידע. ההצהרה הזו לבדה מספיקה לכך
     * מאז HAT-1315 - היא גם מה שהכפתור מושך וגם מה שנמשך בפוקוס (ר'
     * useGlobalRefresh ו-useFocusRefresh).
     */
    meta: { refreshable: true } satisfies RefreshableQueryMeta,
  });

  const [requests, setRequests] = useState<SupportRequestDtoIn[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<SupportRequestDtoIn[]>([]);

  //* Any code (if necessary) for the AdditionalInfo component goes below
  const [detailedRequest, setDetailedRequest] = useState<SupportRequestDtoIn>();

  //* Any code (if necessary) for the AdditionalInfo component goes above

  const [cancelRequest, setCancelRequest] = useState<SupportRequestDtoIn | null>(null);
  const [reduceAmountRequest, setReduceAmountRequest] = useState<SupportRequestDtoIn | null>(null);
  const [moveToNextMonthRequests, setMoveToNextMonthRequests] = useState<SupportRequestDtoIn[]>([]);
  const [signRequest, setSignRequest] = useState<SupportRequestDtoIn | null>(null);
  const [editRequest, setEditRequest] = useState<SupportRequestDtoIn | null>(null);

  const filtersRef = useRef<{ resetFilters: () => void }>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * הפרמטרים שמגיעים מ"דורש טיפול" שב"תמונת מצב", ויחד הם מצמצמים את המסך
   * בדיוק למה שנספר שם: `status` (קבוצות הסטטוס), `waitingOver` (ימים מאז
   * ההגשה), `initialOnly` (עדיין באישור ראשוני - ראו waitingLongerThan/
   * matchesInitialOnly) ו-`dateFrom`/`dateTo` שנקראים באפקט למטה.
   *
   * שלושתם נקראים כאן כמצב התחלתי, מ-searchParams של הראוטר ולא
   * מ-window.location: הקישור מגיע מניווט פנימי, ובו כתובת הדפדפן אינה בהכרח
   * מעודכנת ברגע שהקומפוננטה נבנית. הם גם אינם נמחקים מהכתובת, כך שבנייה
   * מחדש של המסך (טעינת הקרן, רענון, חזרה אחורה) משחזרת את אותה תצוגה.
   * מהרגע שהרכז נוגע בסינון, הוא שולט בו.
   */
  const [initialStatusGroupIds] = useState<string[] | undefined>(() => {
    const raw = searchParams.get('status');
    return raw ? raw.split(',').filter(Boolean) : undefined;
  });

  const [waitingOverDays] = useState<number>(() => {
    const raw = Number(searchParams.get('waitingOver'));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  });

  const [initialOnly] = useState<boolean>(() => searchParams.get('initialOnly') === '1');

  useEffect(() => {
    let incoming = serverRequests ?? [];
    if (waitingOverDays) incoming = waitingLongerThan(incoming, waitingOverDays);
    if (initialOnly) incoming = incoming.filter(isPendingInitialReview);
    setRequests(incoming);
    setFilteredRequests(incoming);
    // Update detailedRequest with fresh data if it exists
    if (detailedRequest && serverRequests) {
      const updated = serverRequests.find((r) => r.correlationId === detailedRequest.correlationId);
      if (updated) {
        setDetailedRequest(updated);
      }
    }
  }, [serverRequests]);

  /*
   * החלפת קרן מנקה את הסינון - אבל לא בטעינה, ולא כשאותה קרן נטענת מחדש.
   *
   * שני דברים שברו כאן סינון שהגיע מהכתובת (`?status=` של "דורש טיפול"):
   * ההרצה הראשונה, שבה אין עדיין ממה לנקות, והתלות באובייקט הקרן במקום במזהה
   * שלה - FundContext מייצר אותו מחדש בזמן הטעינה, וכל אובייקט חדש נראה כמו
   * החלפת קרן וניקה את הסינון מיד אחרי שנקבע.
   */
  const previousFundId = useRef<number | undefined>(undefined);
  useEffect(() => {
    const fundId = currentFund?.id;
    if (previousFundId.current === undefined || previousFundId.current === fundId) {
      previousFundId.current = fundId;
      return;
    }
    previousFundId.current = fundId;
    setDetailedRequest(undefined);
    filtersRef.current?.resetFilters();
  }, [currentFund?.id]);

  /*
   * `?range=all` - נכנסים לעמוד על כל הבקשות ולא רק על החודש הנוכחי.
   *
   * "תמונת מצב" שולחת לכאן מ"דורש טיפול", ובקשה שתקועה באישור מעל שבוע כמעט
   * לעולם אינה של החודש הנוכחי. בלי זה הקישור הגיע לטבלה ריקה, וכל המונים
   * הראו אפס - כאילו אין בקשות כלל.
   *
   * מוחל פעם אחת בטעינה בלבד, ולכן בחירת חודש אחר כך גוברת עליו. הפרמטרים
   * נשארים בכתובת יחד עם `status`, כך שכולם מתארים את מה שמוצג בפועל.
   *
   * `dateFrom`/`dateTo` הם מה ש"תמונת מצב" שולחת בפועל: הכרטיס שם סופר
   * בקשות בתקופה שנבחרה בו, ולכן הקישור פותח בדיוק את אותו חלון. בלעדיו
   * המסך הראה הרבה יותר בקשות מהמספר שנלחץ.
   */
  useEffect(() => {
    const from = searchParams.get('dateFrom');
    const to = searchParams.get('dateTo');
    if (from && to) {
      const dateFrom = new Date(from);
      const dateTo = new Date(to);
      if (!isNaN(dateFrom.getTime()) && !isNaN(dateTo.getTime())) {
        setDateFilter({ dateFrom, dateTo });
        return;
      }
    }
    if (searchParams.get('range') === 'all') setDateFilter(getAllTimeDateRange());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deepLinkExpanded = useRef(false);
  useEffect(() => {
    const resourceId = searchParams.get('resourceId');
    if (!resourceId || !serverRequests) return;
    const request = serverRequests.find((r) => r.correlationId === resourceId);
    if (request) {
      setDetailedRequest(request);
      searchParams.delete('resourceId');
      setSearchParams(searchParams, { replace: true });
    } else if (!deepLinkExpanded.current) {
      deepLinkExpanded.current = true;
      setDateFilter(getAllTimeDateRange());
    } else {
      searchParams.delete('resourceId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [serverRequests]);

  /*
   * HAT-1395: חשבונות שהתשלום אליהם חזר והמשרד מחק אותם. ההודעה בראש הלוח
   * מפנה לבקשות עצמן (מספר בקשה = קישור למגירה); החלון «עדכון חשבון בנק», שמחליף
   * את החשבון בכל הבקשות של הקרן, נפתח מהכפתור במגירה, מתפריט השורה, או מהקישור
   * במייל (`?returnedAccount=<מזהה החשבון>`).
   */
  const { groups: returnedPaymentGroups, isLoaded: returnedPaymentsLoaded, groupOf: returnedPaymentOf } = useReturnedPayments();
  const [fixReturnedPayment, setFixReturnedPayment] = useState<ReturnedPaymentDtoIn | null>(null);
  // The mail's link names an account nobody here waits on any more - another coordinator fixed it,
  // or this one already did. Said, rather than opening the board with no word about the link.
  const [staleReturnedLink, setStaleReturnedLink] = useState(false);

  useEffect(() => {
    const returnedAccount = Number(searchParams.get('returnedAccount'));
    if (!returnedAccount || !returnedPaymentsLoaded) return;
    // Already fixed, or never in this fund: the board says what is left, if anything.
    const group = returnedPaymentGroups.find((g) => g.returnedAccount.id === returnedAccount);
    if (group) setFixReturnedPayment(group);
    else setStaleReturnedLink(true);
    searchParams.delete('returnedAccount');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedPaymentsLoaded, returnedPaymentGroups]);

  const handleReturnedPaymentChanged = () => {
    if (currentFund) queryClient.invalidateQueries({ queryKey: [returnedPaymentsUrl(currentFund.id)] });
    refetchRequests();
  };

  // בקשה נפתחת כמו מקישור: בקשה מחוץ לטווח התאריכים של הלוח מרחיבה אותו קודם,
  // ואז אפקט ה-resourceId למעלה פותח אותה.
  const openRequestById = (correlationId: string) => {
    const row = serverRequests?.find((r) => r.correlationId === correlationId);
    if (row) {
      setDetailedRequest(row);
      return;
    }
    deepLinkExpanded.current = true;
    searchParams.set('resourceId', correlationId);
    setSearchParams(searchParams, { replace: true });
    // «כל הזמן» ends with the current month, and a request whose months all come later is not in it.
    // Its months are known here, so the range reaches the last of them.
    const allTime = getAllTimeDateRange();
    const lastMonth = returnedPaymentGroups
      .flatMap((group) => group.requests)
      .find((request) => request.correlationId === correlationId)
      ?.months.reduce((last, month) => Math.max(last, new Date(month.requestedForDate).getTime()), 0);
    const last = lastMonth ? new Date(lastMonth) : null;
    const dateTo = last && last > allTime.dateTo ? new Date(last.getFullYear(), last.getMonth() + 1, 0) : allTime.dateTo;
    const sameRange =
      formatISODate(dateFilter.dateFrom) === formatISODate(allTime.dateFrom) && formatISODate(dateFilter.dateTo) === formatISODate(dateTo);
    // The same range would fetch nothing new, and the request would never open: fetch again instead.
    if (sameRange) refetchRequests();
    else setDateFilter({ dateFrom: allTime.dateFrom, dateTo });
  };

  /*
   * ------------------------------------------------------------
   *  פעולות השורה — מקור אחד לשתי התצוגות
   * ------------------------------------------------------------
   * חולצו מתוך תא הפעולות כדי שהטבלה והכרטיס יקראו את **אותה**
   * רשימה. עותק שני היה הדרך הקצרה ביותר לכך שפעולה תתקיים
   * במסך אחד ותיעדר בשני, וזה בדיוק מה שהמעבר לנייד לא אמור
   * לעשות (HAT-1170).
   */
  const isTransferReceiptEligible = (request: SupportRequestDtoIn) =>
    request.type === SupportRequestType.OneTime && request.statuses[0] === 'Done';

  const buildRequestMenuItems = (requestRow: SupportRequestDtoIn) => {
    const menuItems = [];
    // HAT-1395: first, because it is what the request is waiting for.
    const returnedPayment = returnedPaymentOf(requestRow.correlationId);
    if (returnedPayment) {
      menuItems.push({
        icon: bankIcon,
        text: t('returnedPayment.menuLabel'),
        onClick: () => setFixReturnedPayment(returnedPayment),
      });
    }
    // SPR-1054: while the fund's 2-signature requirement holds this request back, a
    // representative can sign it (moving it toward the office) or edit amount/beneficiary
    // (which resets any signatures already collected). HAT-1345: cancelling is now offered here
    // too - a request that never reached the office is the easiest one to drop, and leaving it
    // out stranded the beneficiary's bank account behind a request nothing could close.
    if (requestRow.statuses.includes('PendingSignatures')) {
      menuItems.push({
        icon: signatureIcon,
        text: t('signSupportRequest.menuLabel'),
        onClick: () => setSignRequest(requestRow),
      });
      menuItems.push({
        icon: editIcon,
        text: t('editSupportRequest.menuLabel'),
        onClick: () => setEditRequest(requestRow),
      });
    }
    // HAT-1398: lowering the amount, until the office hands the request over for payment. The
    // server decides per month (canReduceAmount); a request awaiting signatures never gets it
    // here - "ערוך סכום" above already covers it, and resets the signatures.
    if (hasReducibleMonth(requestRow)) {
      menuItems.push({
        icon: editIcon,
        text: t('reduceSupportRequestAmount.menuLabel'),
        onClick: () => setReduceAmountRequest(requestRow),
      });
    }
    if (hasCancellableMonth(requestRow)) {
      menuItems.push({
        icon: deleteIcon,
        text: t('cancelSupportRequest.menuLabel'),
        onClick: () => setCancelRequest(requestRow),
      });
    }
    if (canMoveToNextMonth(requestRow)) {
      menuItems.push({
        icon: calendarIcon,
        text: t('moveToNextMonth.menuLabel'),
        onClick: () => setMoveToNextMonthRequests([requestRow]),
      });
    }
    return menuItems;
  };

  /*
   * הורדת האסמכתא, במנותק מהכפתור שנשא אותה.
   *
   * ⚠️ הפעולה עצמה זהה לזו שב-ReceiptDownloadButton (אותו מסלול,
   * אותו שם קובץ). מה שהיא לא נושאת הוא מצב ה-isDownloading של אותו
   * כפתור — בגיליון אין כפתור להשבית, והגיליון נסגר ברגע שנוגעים בו.
   */
  const downloadTransferReceipt = (request: SupportRequestDtoIn) => {
    if (!currentFund?.id) return;
    void downloadFileFromApi(
      `funds/${currentFund.id}/support-requests/${request.correlationId}/receipt`,
      `אסמכתא-${getShortRequestId(request.correlationId)}.pdf`,
      currentUser?.token ?? ''
    );
  };

  // HAT-1210 / SPR-1203: dual Gregorian|Hebrew date follows board calendarMode.
  const dualDateCell = Cell<SupportRequestDtoIn>(cellType.Date, { dualCalendar: true });
  const columns: ColumnDef<SupportRequestDtoIn>[] = [
    // Selection column - enables the bulk "move to next month" action on rejected requests
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: Cell(cellType.Selection),
      meta: {
        className: 'max-w-[48px] min-w-[48px] w-[48px]',
        hiddenOnMobile: true,
        lockLayout: true,
      },
    },
    {
      id: 'requestId',
      header: Col(t('requestId')),
      cell: ({ row }) => `${getShortRequestId(row.original.correlationId)}`,
      accessorFn: (row) => getShortRequestId(row.correlationId),
      meta: {
        className: 'max-w-[72px] min-w-[72px] w-[72px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'goal',
      header: Col(t('goal')),
      cell: ({ getValue }) => {
        const goalName = getValue() as string;
        const mobileKey = `mobileGoalNames.${goalName}`;
        const displayName = isMobile && t(mobileKey) !== mobileKey ? t(mobileKey) : t(goalName);
        return <div className={tableStyles.cellText}>{displayName}</div>;
      },
      accessorFn: (row) => row.goal?.name ?? '',
      meta: {
        className: 'min-w-[140px]',
        mobileClassName: 'max-w-[22%] min-w-[22%] w-[22%]',
      },
    },
    {
      id: 'supported',
      header: Col(t('supported')),
      // Fund-expense rows have no BeneficiaryContact - render the supplier (or payer, when
      // the fund reimburses a private payer) name with an info tooltip that says which one
      // it is (mirrors the management view's "recipientName" column). Beneficiary rows keep
      // the existing styled Supported cell.
      cell: ({ row, getValue }) => {
        const isFundExpense = row.original.goal?.allowedFor === AllowedFor.Fund;
        if (isFundExpense) {
          const details = row.original.additionalDetails as FundExpenseDetails | undefined;
          const name = getValue() as string;
          const tooltipText = details?.isAlreadyPaid ? t('payer') : t('supplier');
          const tooltipId = `recipient-info-${row.id}`;
          return (
            <div className="flex items-center gap-1.5 min-w-0">
              <img id={tooltipId} src={infoIcon} alt="" className="w-4 h-4 shrink-0" />
              <CustomTooltip targetId={tooltipId}>
                <span className="body-xs-sm-regular">{tooltipText}</span>
              </CustomTooltip>
              <span className={tableStyles.cellText} style={{ fontWeight: 600 }}>
                {name}
              </span>
            </div>
          );
        }
        const { firstName, lastName } = getValue() as { firstName: string; lastName: string };
        return (
          <a className={`text-(--font-link) ${tableStyles.cellText}`} style={{ fontWeight: 600, textDecoration: 'underline' }}>
            {firstName} {lastName}
          </a>
        );
      },
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          return getFundExpenseRecipientName(row);
        }
        return { firstName: row.supportedContact?.firstName ?? '', lastName: row.supportedContact?.lastName ?? '' };
      },
      meta: {
        className: 'max-w-[152px] min-w-[140px] w-[152px]',
        mobileClassName: 'max-w-[28%] min-w-[28%] w-[28%]',
      },
    },
    {
      id: 'sum',
      header: Col(t('amount')),
      cell: Cell(cellType.Amount),
      accessorFn: (row) => ({ amount: row.amount, currency: 'ILS' }),
      meta: {
        className: 'max-w-[100px] min-w-[100px] w-[100px]',
        mobileClassName: 'max-w-[25%] min-w-[25%] w-[25%]',
      },
    },
    {
      id: 'invoiceNumber',
      header: Col(t('invoiceNumber')),
      cell: Cell(cellType.String),
      accessorFn: (row) => row.invoiceNumber ?? '',
      meta: {
        className: 'max-w-[88px] min-w-[88px] w-[88px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'paymentMethod',
      header: Col(t('paymentMethod')),
      cell: Cell(cellType.PaymentWithType),
      accessorFn: (row) => ({
        method: row.paymentMethodType,
        isMonthly: row.type === SupportRequestType.Monthly,
      }),
      meta: {
        className: 'max-w-[112px] min-w-[112px] w-[112px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'date',
      header: Col(t('submissionDate')),
      cell: dualDateCell,
      accessorKey: 'requestedAt',
      meta: {
        className: 'max-w-[104px] min-w-[104px] w-[104px]',
        mobileClassName: 'max-w-[25%] min-w-[25%] w-[25%]',
      },
    },

    {
      id: 'status',
      header: Col(t('status')),
      cell: Cell(cellType.Status),
      accessorFn: (row) => getAggregatedStatus(row.statuses),
      meta: {
        className: 'max-w-[132px] min-w-[132px] w-[132px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'rowActions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <SupportRequestRowActions
          request={row.original}
          fundId={currentFund?.id}
          isMobile={isMobile}
          isReceiptEligible={isTransferReceiptEligible(row.original)}
          menuItems={buildRequestMenuItems(row.original)}
        />
      ),
      meta: {
        className: rowActionsWidth,
        mobileClassName: 'max-w-[20%] min-w-[20%] w-[20%]',
        lockLayout: true,
      },
    },
  ];

  const exportColumns: ColumnDef<SupportRequestDtoIn>[] = [
    {
      id: 'requestId',
      header: 'requestId',
      accessorFn: (row) => getShortRequestId(row.correlationId),
    },
    {
      id: 'goal',
      header: 'goal',
      accessorFn: (row) => t(row.goal?.name ?? ''),
    },
    {
      id: 'supported',
      header: 'supported',
      // Export keeps the same fallback the UI uses: supported full name for beneficiary
      // rows, supplier/payer name for fund-expense rows. Without this, fund-expense rows
      // exported as empty strings.
      accessorFn: (row) => {
        if (row.goal?.allowedFor === AllowedFor.Fund) {
          return getFundExpenseRecipientName(row);
        }
        return row.supportedContact ? `${row.supportedContact.firstName} ${row.supportedContact.lastName}` : '';
      },
    },
    {
      id: 'amount',
      header: 'amount',
      accessorFn: (row) => row.amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' }),
    },
    {
      id: 'invoiceNumber',
      header: 'invoiceNumber',
      accessorFn: (row) => row.invoiceNumber ?? '',
    },
    {
      id: 'paymentMethod',
      header: 'paymentMethod',
      accessorFn: (row) => (row.type === SupportRequestType.Monthly ? t('monthly') : t('oneTime')),
    },
    {
      id: 'date',
      header: 'submissionDate',
      accessorKey: 'requestedAt',
    },
    {
      id: 'status',
      header: 'status',
      accessorFn: (row) => {
        const aggregatedStatus = getAggregatedStatus(row.statuses);
        const group = getStatusGroup(aggregatedStatus);
        return t(group?.id || aggregatedStatus);
      },
    },
    // --- "status and documents" tab data (fetched in bulk when the user clicks export) ---
    {
      id: 'transferDate',
      header: 'supportRequestsExport.transferDate',
      accessorFn: (row) => {
        const subRequests = getRowExportDetails(row)?.supportRequests ?? [];
        // A single raw ISO date becomes a real Excel date cell; monthly requests join all
        // payment dates into one text cell instead.
        if (subRequests.length === 1) return subRequests[0].requestedForDate;
        return joinPerPayment(getRowExportDetails(row), (subRequest) => formatDateLocale(subRequest.requestedForDate, i18n.language));
      },
    },
    {
      id: 'requestedAmount',
      header: 'requestedAmount',
      accessorFn: (row) => {
        const subRequests = getRowExportDetails(row)?.supportRequests ?? [];
        // A single currency-formatted value becomes a numeric currency cell; joined monthly
        // values stay plain text (no ₪ symbol, so Excel doesn't misparse them as one number).
        if (subRequests.length === 1) return subRequests[0].requestedAmount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
        return joinPerPayment(getRowExportDetails(row), (subRequest) => subRequest.requestedAmount.toLocaleString('he-IL'));
      },
    },
    {
      id: 'approvedAmount',
      header: 'approvedAmount',
      accessorFn: (row) => {
        const subRequests = getRowExportDetails(row)?.supportRequests ?? [];
        if (subRequests.length === 1) return subRequests[0].approvedAmount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
        return joinPerPayment(getRowExportDetails(row), (subRequest) => subRequest.approvedAmount.toLocaleString('he-IL'));
      },
    },
    {
      id: 'paymentsStatus',
      header: 'supportRequestsExport.paymentsStatus',
      accessorFn: (row) => joinPerPayment(getRowExportDetails(row), (subRequest) => t(`requestDetailStatus.${getDetailStatusDisplayId(subRequest.status)}`)),
    },
    {
      id: 'documents',
      header: 'documentsAndApprovals',
      accessorFn: (row) => {
        const describeDocument = (document: DocumentWithDetailsDtoIn) => {
          const parts = [`${t('uploadDate')}: ${formatDateLocale(String(document.creationTime), i18n.language)}`];
          if (document.status === DocumentStatus.Rejected) parts.push(`${t('status')}: ${t('documentStatus.Rejected')}`);
          if (document.officeComment) parts.push(`${t('officeComment')}: ${document.officeComment}`);
          if (document.userComment) parts.push(`${t('userComment')}: ${document.userComment}`);
          return `${document.name} (${parts.join(', ')})`;
        };
        return (getRowExportDetails(row)?.documents ?? []).map(describeDocument).join(' | ');
      },
    },
    {
      id: 'officeComments',
      header: 'officeComments.title',
      accessorFn: (row) =>
        (getRowExportDetails(row)?.visibleOfficeComments ?? [])
          .map((comment) => `${formatDateLocale(comment.date, i18n.language)}: ${comment.comment}`)
          .join(' | '),
    },
    // --- "additional details" tab data ---
    {
      id: 'accountOwnerName',
      header: 'supportRequestsExport.accountOwnerName',
      accessorFn: (row) => getRowExportDetails(row)?.paymentInfo?.accountOwnerName ?? '',
    },
    {
      id: 'bank',
      header: 'supportRequestsExport.bank',
      accessorFn: (row) => {
        const paymentInfo = getRowExportDetails(row)?.paymentInfo;
        if (!paymentInfo) return '';
        return [paymentInfo.bankName, paymentInfo.bankNumber || ''].filter(Boolean).join(' ');
      },
    },
    {
      id: 'branchNumber',
      header: 'supportRequestsExport.branchNumber',
      accessorFn: (row) => getRowExportDetails(row)?.paymentInfo?.branchNumber ?? '',
    },
    {
      id: 'accountNumber',
      header: 'supportRequestsExport.accountNumber',
      accessorFn: (row) => getRowExportDetails(row)?.paymentInfo?.accountNumber ?? '',
    },
    // Fund-expense-only fields (empty for beneficiary requests) - already present on the list DTO
    {
      id: 'supplierPaymentStatus',
      header: 'supportRequestsExport.supplierPaymentStatus',
      accessorFn: (row) => {
        const fundExpenseDetails = getRowFundExpenseDetails(row);
        if (!fundExpenseDetails) return '';
        return fundExpenseDetails.isAlreadyPaid ? t('alreadyPaid') : t('notYetPaid');
      },
    },
    {
      id: 'supplierName',
      header: 'supplierName',
      accessorFn: (row) => (row.goal?.allowedFor === AllowedFor.Fund ? (getSupplierDisplayDetails(row.supplier, row.additionalDetails)?.name ?? '') : ''),
    },
    {
      id: 'supplierTaxId',
      header: 'supplierTaxId',
      accessorFn: (row) => (row.goal?.allowedFor === AllowedFor.Fund ? (getSupplierDisplayDetails(row.supplier, row.additionalDetails)?.taxId ?? '') : ''),
    },
    {
      id: 'payerName',
      header: 'payerName',
      accessorFn: (row) => getRowFundExpenseDetails(row)?.payerName ?? '',
    },
    {
      id: 'payerIsraeliId',
      header: 'payerIsraeliId',
      accessorFn: (row) => getRowFundExpenseDetails(row)?.payerIsraeliId ?? '',
    },
  ];

  /**
   * Fetches the additional-info data for the whole date range in one call and merges it into the
   * currently filtered rows, so the exported Excel includes everything shown in the blade's
   * "status and documents" / "additional details" tabs (SPR-1026).
   */
  const getExportData = async (): Promise<SupportRequestDtoIn[]> => {
    if (!currentFund?.id) return filteredRequests;

    const params = new URLSearchParams({
      dateFrom: formatISODate(dateFilter.dateFrom),
      dateTo: formatISODate(dateFilter.dateTo),
    });
    const exportDetails = await getAuth<SupportRequestExportDetailsDtoIn[]>(`funds/${currentFund.id}/support-requests/export-details?${params.toString()}`, {
      Authorization: `Bearer ${currentUser?.token}`,
    });

    const detailsByCorrelationId = new Map(exportDetails.map((details) => [details.correlationId, details]));
    return filteredRequests.map(
      (request): SupportRequestExportRow => ({
        ...request,
        exportDetails: detailsByCorrelationId.get(request.correlationId),
      })
    );
  };

  //* Any code (if necessary) for the FiltersArea component goes below
  // Do not mind if you have to create a new state etc., just add it below.
  // Moving them upwards will be done in a separate step
  //* Any code (if necessary) for the FiltersArea component goes above

  return (
    <div>
      {currentFund ? (
        <>
          {/* ניווט הלוחות במסך צר, בשפת נדרים. מחזיר null מעל 1100. */}
          <BoardTabs />
          {detailedRequest && currentFund && (
            <AdditionalInfo
              request={detailedRequest}
              handleXclick={() => setDetailedRequest(undefined)}
              onSupportedNameClick={setSelectedSupportedName}
              onCompletionSuccess={refetchRequests}
              onUpdateBankAccount={setFixReturnedPayment}
            />
          )}

          <ReturnedPaymentDialog
            key={fixReturnedPayment?.returnedAccount.id}
            fundId={currentFund.id}
            group={fixReturnedPayment}
            onClose={() => setFixReturnedPayment(null)}
            onChanged={handleReturnedPaymentChanged}
          />

          <DataTable
            /*
             * HAT-1365: the page scrolls, not a box inside it - like transactions and standing
             * orders beside it in the tab row. The fixed-height box lost its chrome (toolbar,
             * filters, two rows of status chips, column titles) out of its own height, so on a
             * 674px laptop window it showed five rows behind a second, inner scrollbar and read
             * as a table that does not scroll. Now the chrome scrolls away, the column titles
             * stick, and the pin keeps the controls in view when they are wanted.
             */
            scrollMode="document"
            title={t('supportRequests')}
            data={filteredRequests}
            getRowId={(row) => row.correlationId}
            isLoading={isLoading}
            columns={columns}
            exportColumns={exportColumns}
            getExportData={getExportData}
            exportFilename={t('supportRequests')}
            skeletonConfig={supportRequestSkeletonConfig}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            density={density}
            onDensityChange={setDensity}
            showCalendarMode
            columnLayoutKey="supportRequests"
            searchPlaceholder="searchRequest"
            notice={
              returnedPaymentGroups.length > 0 || staleReturnedLink ? (
                <div className="flex flex-col gap-2">
                  {staleReturnedLink && (
                    <Alert type="info" inline body={t('returnedPayment.alreadyFixedNotice')} onClose={() => setStaleReturnedLink(false)} />
                  )}
                  {returnedPaymentGroups.length > 0 && (
                    <ReturnedPaymentsNotice groups={returnedPaymentGroups} onOpenRequest={openRequestById} onUpdateBankAccount={setFixReturnedPayment} />
                  )}
                </div>
              ) : undefined
            }
            filters={
              <FiltersArea
                requests={requests ?? []}
                setFilteredRequests={setFilteredRequests}
                selectedSupportedName={selectedSupportedName}
                setSelectedSupportedName={setSelectedSupportedName}
                dateFrom={dateFilter.dateFrom}
                dateTo={dateFilter.dateTo}
                onDateChange={(dateFrom, dateTo) => setDateFilter({ dateFrom, dateTo })}
                initialStatusGroupIds={initialStatusGroupIds}
                isBusy={isLoading}
                /*
                 * ⚠️ במסך צר הפעולה לא נעלמת — היא **עוברת מקום**.
                 * כאן היא הייתה נדחקת בין הכותרת, החיפוש והסינון,
                 * ואחרי גלילה למטה כבר לא הייתה בהישג יד. במקומה
                 * מופיעים כרטיס הפעולה מעל הרשימה והכפתור הצף, שני
                 * אלה יחד כמו במקור (ר' components/ui/Fab).
                 */
                actionButton={
                  isMobile ? undefined : (
                    <div className={styles.toolbarActions}>
                      {/* Requests filled in where they could not be sent, waiting to be finished
                          here. Renders nothing when the feature is off or there are none. */}
                      <DraftsButton
                        onContinue={(draft) => {
                          setContinuedDraft(draft);
                          openNewRequest();
                        }}
                      />
                      {/* The tooltip hangs on the wrapper: a disabled button gets no hover of its own. */}
                      <span id="newSupportRequestButton" className={styles.addButtonWrap}>
                        <Button
                          variant="primary"
                          onClick={openNewRequest}
                          className={`${styles.addButton} ${isViewOnly && !isFundFrozen ? styles.addButtonLocked : ''}`}
                          disabled={isFundFrozen || isViewOnly}
                          title={isFundFrozen ? t('frozenFundNotice.cannotRequest') : undefined}
                        >
                          {isViewOnly && !isFundFrozen && <LockIcon />}
                          {t('addSupportRequest')}
                        </Button>
                      </span>
                      {isViewOnly && !isFundFrozen && (
                        <CustomTooltip targetId="newSupportRequestButton" placement="bottom">
                          {t('fundPeople.viewOnlyCannotWrite')}
                        </CustomTooltip>
                      )}
                    </div>
                  )
                }
                ref={filtersRef}
              />
            }
            handleRowClick={(row) => setDetailedRequest(row)}
            /*
             * תצוגת המסך הצר. הכרטיס מחזיר את שש העמודות שמסומנות
             * hiddenOnMobile ואת הורדת האסמכתא — ר' SupportRequestCard.
             *
             * ⚠️ 'boxed', בכוונה. בעיצוב המקורי (hatzdaka-nedarim)
             * בקשות תמיכה הן דווקא הלוח היחיד עם כרטיס-במיכל
             * (card-list-boxed); שני הלוחות השכנים בסרגל הטאבים —
             * תנועות והוראות קבע — הם שורה שטוחה. ניסיון קודם לאחד
             * את שלושתם ל-'flat' התברר כסטייה מהמקור ולא כתיקון: מה
             * שהמשתמשת ביקשה זה שכל לוח יהיה נאמן לעיצוב המקורי שלו,
             * לא שלד זהה לשלושתם. ר' ההערה בראש SupportRequestCard.
             */
            hideTitleOnCards={hasBoardTabs}
            cardLayout="boxed"
            cardListHeader={
              <div className={styles.mobileCardHeader}>
                {/* Same errand as the "new request" card below it, and the only way to reach a
                    saved draft on a narrow screen - the toolbar it normally lives in does not
                    render here at all. Renders nothing when the feature is off or there are
                    none, exactly like the desktop toolbar copy. */}
                <DraftsButton
                  onContinue={(draft) => {
                    setContinuedDraft(draft);
                    openNewRequest();
                  }}
                />
                {/* Not merely disabled: on a narrow screen this card IS the action, and a dead card
                    with no room for an explanation beside it reads as a broken screen. The banner
                    above the board carries the reason. */}
                {!isFundFrozen && !isViewOnly && (
                  <FabActionCard label={t('submitNewSupportRequest')} onClick={openNewRequest} />
                )}
              </div>
            }
            renderCard={(row, { selectionMode }) => (
              <SupportRequestCard
                row={row}
                selectionMode={selectionMode}
                isActive={row.original === detailedRequest}
                onOpen={setDetailedRequest}
                fundExpenseRecipientName={getFundExpenseRecipientName}
                menuItems={buildRequestMenuItems(row.original)}
                isReceiptEligible={isTransferReceiptEligible(row.original)}
                onDownloadReceipt={() => downloadTransferReceipt(row.original)}
              />
            )}
            activeRecord={detailedRequest}
            customGlobalSearch={requestSearch}
            selectionActions={[
              {
                label: t('moveToNextMonth.bulkLabel'),
                onClick: (selectedRows) => setMoveToNextMonthRequests(selectedRows),
                isVisible: (selectedRows) => selectedRows.length > 0 && selectedRows.every(canMoveToNextMonth),
              },
            ]}
          />
          {/* אין כפתור צף: במסך צר הפעולה יושבת בכרטיס "הגשת בקשת תמיכה
              חדשה" שמעל הרשימה (cardListHeader), וזה מספיק — החלטת המוצר
              בסבב הביקורת של HAT-1170. */}
          <NewSupportRequest
            open={openNewSupportRequest}
            setOpen={setOpenNewSupportRequest}
            draft={continuedDraft}
            onDraftHandled={() => setContinuedDraft(null)}
            onRefreshList={refetchRequests} />

          <CancelSupportRequestDialog
            key={currentFund?.id}
            open={cancelRequest !== null}
            onClose={() => setCancelRequest(null)}
            request={cancelRequest}
            onSuccess={refetchRequests}
          />

          <ReduceSupportRequestAmountDialog
            key={`reduce-${currentFund?.id}`}
            open={reduceAmountRequest !== null}
            onClose={() => setReduceAmountRequest(null)}
            request={reduceAmountRequest}
            onSuccess={refetchRequests}
          />

          <MoveToNextMonthDialog
            open={moveToNextMonthRequests.length > 0}
            onClose={() => setMoveToNextMonthRequests([])}
            requests={moveToNextMonthRequests}
            onSuccess={refetchRequests}
          />

          <SignSupportRequestDialog
            key={`sign-${currentFund?.id}`}
            open={signRequest !== null}
            onClose={() => setSignRequest(null)}
            request={signRequest}
            onSuccess={refetchRequests}
          />

          <EditSupportRequestDialog
            key={`edit-${currentFund?.id}`}
            open={editRequest !== null}
            onClose={() => setEditRequest(null)}
            request={editRequest}
            onSuccess={refetchRequests}
          />
        </>
      ) : (
        <h1>{t('noFundSelected')}.</h1>
      )}
    </div>
  );
};

const SupportRequestsPageWithCalendar = () => (
  <CalendarModeProvider>
    <SupportRequestsPage />
  </CalendarModeProvider>
);

export default SupportRequestsPageWithCalendar;
